import React, { useState, useRef } from 'react';
import useOrders from '../hooks/useOrders';
import Pagination from '../components/common/Pagination';
import OrdersActions from '../components/orders/OrdersActions';
import Orderstable from '../components/orders/Orderstable';
import GenerateChallanModal from '../components/delivery-orders/GenerateChallanModal';
import OrdersForm from '../components/orders/OrdersForm';
import OrdersViewModal from '../components/orders/OrdersViewModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SuperAdminDeleteDialog from '../components/common/SuperAdminDeleteDialog';
import MessageAlert from '../components/common/MessageAlert';
import { useErrorDialog } from '../context/errorDialogContext';
import { UI_COLORS, colorHelpers } from '../utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import ordersService from '../services/ordersService';
import { useRole } from '../context/roleContext';
import { useWarehouseContext } from '../context/WarehouseContext';
import { getDefaultDateRange } from '../utils/format';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'orders_page_size';
const DEFAULT_ORDER_STATUS_FILTER = 'pending';

const OrdersPage = () => {
  const { t } = useTranslation();
  const {
    orders,
    loading,
    error,
    fetchOrders,
    totalCount,
    totalPages,
    createOrder,
    updateOrder,
    deleteOrder,
    bulkDeleteOrders,
    deleteAllOrders,
    generateDeliveryChallan,
  } = useOrders();

  const { checkDeleteAccess } = useRole();

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);


  const [showGenerateChallanModal, setShowGenerateChallanModal] = useState(false);
  const [generateChallanOrderId, setGenerateChallanOrderId] = useState(null);

  const [statusLoading] = useState(new Set());
  const [viewingOrder, setViewingOrder] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [committedSearch, setCommittedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(DEFAULT_ORDER_STATUS_FILTER);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
  });
  const searchDebounceRef = useRef(null);

  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());
  const { from_date: fromDate, to_date: toDate } = dateRange;

  const handleFromDateChange = (val) => {
    setDateRange(prev => ({ ...prev, from_date: val }));
    setCurrentPage(1);
  };

  const handleToDateChange = (val) => {
    setDateRange(prev => ({ ...prev, to_date: val }));
    setCurrentPage(1);
  };

  const strings = t('orders', { returnObjects: true });
  const { showError } = useErrorDialog();
  const { selectedWarehouseIds } = useWarehouseContext();

  // Debounce search → committedSearch
  React.useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCommittedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchTerm]);

  // Reset page on status filter change
  React.useEffect(() => { setCurrentPage(1); }, [statusFilter]);

  // Fetch orders
  React.useEffect(() => {
    const skip = (currentPage - 1) * pageSize;
    const params = { skip, limit: pageSize, from_date: fromDate, to_date: toDate };
    if (statusFilter && statusFilter !== 'all') {
      params.status = [statusFilter];
    }
    if (committedSearch) params.search = committedSearch;
    void fetchOrders(params, { force: true });
  }, [currentPage, committedSearch, statusFilter, fetchOrders, selectedWarehouseIds, pageSize, fromDate, toDate]);

  React.useEffect(() => {
    if (error) showError(error, strings.page?.alerts?.errorTitle);
  }, [error, showError, strings.page]);


  const selectOrder = (id, checked) => {
    setSelectedOrders(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)));
  };
  const selectAllOrders = (checked) => {
    const currentPageIds = orders.map(i => i.id);
    if (checked) {
      setSelectedOrders(prev => {
        const set = new Set([...prev, ...currentPageIds]);
        return Array.from(set);
      });
    } else {
      setSelectedOrders(prev => prev.filter(id => !currentPageIds.includes(id)));
    }
  };

  const hasSelection = selectedOrders.length > 0;
  const selectionCount = selectedOrders.length;

  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      const skip = (currentPage - 1) * pageSize;
      const params = { skip, limit: pageSize, from_date: fromDate, to_date: toDate };
      if (statusFilter && statusFilter !== 'all') {
        params.status = [statusFilter];
      }
      if (committedSearch) params.search = committedSearch;
      await fetchOrders(params, { force: true });
    } catch (err) {
      console.error('Failed to refresh orders', err);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
  };







  const handleGenerateChallanFor = (orderId) => {
    // Open modal instead of direct generation
    setGenerateChallanOrderId(orderId);
    setShowGenerateChallanModal(true);
  };

  const handleConfirmGenerateChallan = async (orderId, payload) => {
    // payload: { challan_date, expenses, expense_remark, discount, discount_remark }
    setActionLoading(true);
    try {
      const result = await generateDeliveryChallan(orderId, payload);
      if (result.success) {
        setShowGenerateChallanModal(false); // Close modal on success
        setGenerateChallanOrderId(null);
        setAlertMessage({ type: 'success', title: t('common.status.success'), message: 'Challan generated successfully' });
        try { await fetchOrders({}, { force: true }); } catch (e) { console.error('Failed to refresh after challan', e); }
      } else {
        setAlertMessage({ type: 'error', title: t('common.status.failed'), message: result.error || 'Failed to generate challan' });
      }
    } catch (err) {
      console.error('Generate challan failed', err);
      setAlertMessage({ type: 'error', title: t('common.status.failed'), message: err.message || 'Failed to generate challan' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateOrder = () => {
    setEditingOrder(null);
    setEditingOrderId(null);
    setShowOrderForm(true);
  };

  const handleEditOrder = async (orderId) => {
    setEditingOrder(null);
    setEditingOrderId(orderId);
    setShowOrderForm(true);
  };

  const handleViewOrder = async (orderId) => {
    setViewLoading(true);
    setViewingOrder(null);
    setShowViewModal(true);
    try {
      const result = await ordersService.getOrderById(orderId, { force: true });
      if (result.success && result.data) setViewingOrder(result.data);
      else showError(result.error || t('orders.alerts.fetchFailed'), strings.page?.alerts?.errorTitle || t('orders.page.alerts.errorTitle'));
    } catch (err) {
      console.error('View order failed', err);
      showError(err.message || t('orders.alerts.fetchFailed'), strings.page?.alerts?.errorTitle || t('orders.page.alerts.errorTitle'));
    } finally {
      setViewLoading(false);
    }
  };

  const handleDeleteOrder = (orderId) => {
    if (!checkDeleteAccess()) return;
    const order = (orders || []).find(i => i.id === orderId) || null;
    const displayName = order?.customer_name || strings.table?.copy?.noCustomer;
    setDeleteConfirm({
      type: 'single',
      order,
      orderId,
      title: strings.table?.actions?.delete || strings.table?.columns?.actions,
      message: `${strings.table?.columns?.customer || t('common.table.empty.title')}: ${displayName}. This will also delete all associated sub-orders and delivery orders.`,
      confirmLabel: t('common.dialogs.delete.confirm') || t('common.buttons.delete'),
      cancelLabel: t('common.dialogs.delete.cancel') || t('common.buttons.cancel'),
      confirmVariant: 'danger',
    });
  };

  const handleBulkDelete = () => {
    if (!checkDeleteAccess() || selectedOrders.length === 0) return;
    setShowBulkDeleteModal(true);
  };

  const handleConfirmBulkDelete = async (password) => {
    setActionLoading(true);
    try {
      const result = await bulkDeleteOrders(selectedOrders || [], password);
      if (result.success) {
        setShowBulkDeleteModal(false);
        setSelectedOrders([]);
      } else {
        setAlertMessage({ type: 'error', title: t('common.status.failed'), message: result.error || 'Failed to bulk delete orders' });
      }
    } catch (err) {
      console.error('Bulk delete failed', err);
      setAlertMessage({ type: 'error', title: t('common.status.failed'), message: err.message || 'Failed to bulk delete orders' });
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelete = async () => {
    setActionLoading(true);
    try {
      let result;
      if (deleteConfirm.type === 'single') {
        const id = deleteConfirm.orderId || deleteConfirm.order?.id;
        result = await deleteOrder(id);
      } else {
        result = await bulkDeleteOrders(selectedOrders || []);
      }

      if (result.success) {
        setDeleteConfirm(null);
        setSelectedOrders([]);
      } else {
        setAlertMessage({ type: 'error', title: t('common.status.error'), message: result.error || t('orders.alerts.deleteFailed') });
      }
    } catch (err) {
      console.error('Delete failed', err);
      setAlertMessage({ type: 'error', title: t('common.status.error'), message: err.message || t('orders.alerts.deleteFailed') });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDeleteAll = async (password) => {
    setActionLoading(true);
    try {
      const result = await deleteAllOrders(password);
      if (result.success) {
        setShowDeleteAllModal(false);
        setSelectedOrders([]);
      } else {
        setAlertMessage({ type: 'error', title: t('common.status.failed'), message: result.error || 'Failed to delete all orders' });
      }
    } catch (err) {
      console.error('Delete all failed', err);
      setAlertMessage({ type: 'error', title: t('common.status.failed'), message: err.message || 'Failed to delete all orders' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleFormSubmit = async (payload) => {
    setActionLoading(true);
    try {
      let result;
      if (editingOrder) {
        result = await updateOrder(editingOrder.id, payload);
      } else if (editingOrderId) {
        result = await updateOrder(editingOrderId, payload);
      } else {
        result = await createOrder(payload);
        if (result.success) {
          await fetchOrders({}, { force: true });
        }
      }
      if (result.success) {
        setShowOrderForm(false);
        setEditingOrder(null);
        setEditingOrderId(null);
      } else {
        setAlertMessage({ type: 'error', title: t('common.status.error'), message: result.error || t('orders.alerts.operationFailed') });
      }
      return result;
    } catch (err) {
      console.error('Form submit failed', err);
      return { success: false, error: err.message };
    } finally {
      setActionLoading(false);
    }
  };

  const totalOrdersCount = totalCount;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {alertMessage && (
        <div className="fixed top-4 right-4 z-50">
          <MessageAlert type={alertMessage.type} title={alertMessage.title} message={alertMessage.message} onClose={() => setAlertMessage(null)} />
        </div>
      )}

      {actionLoading && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.OVERLAY, 0.7) }}
        >
          <div className="flex flex-col items-center">
            <div
              className="h-16 w-16 animate-spin rounded-full border-4 border-b-transparent"
              style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.7) }}
            ></div>
            <p className="mt-4 text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              Loading...
            </p>
          </div>
        </div>
      )}

      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY || '#05445E' }}>
            {strings.page?.header?.title || 'Orders'}
          </h1>
          <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY || '#189AB4' }}>
            {strings.page?.header?.subtitle || 'Manage sales orders, delivery and invoices'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div
            className="flex items-center gap-3 rounded-2xl border px-4 py-3"
            style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
          >
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.15),
                color: UI_COLORS.BUTTONS.PRIMARY.background,
              }}
            >
              <FontAwesomeIcon icon={faBox} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {strings.page?.stats?.total}
              </p>
              <p className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {totalOrdersCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      <OrdersActions
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onAddOrder={handleCreateOrder}
        onRefresh={handleRefresh}
        onBulkDelete={handleBulkDelete}
        onDeleteAll={() => setShowDeleteAllModal(true)}
        hasSelection={hasSelection}
        selectionCount={selectionCount}
        loading={actionLoading}
        refreshLoading={refreshLoading}
        statusValue={statusFilter}
        onStatusChange={setStatusFilter}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={handleFromDateChange}
        onToDateChange={handleToDateChange}
      />

      <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
        <Orderstable
          orders={orders}
          loading={loading}
          selectedOrders={selectedOrders}
          onSelectOrder={selectOrder}
          onSelectAll={selectAllOrders}
          onEditOrder={handleEditOrder}
          onDeleteOrder={handleDeleteOrder}
          onViewOrder={handleViewOrder}


          onGenerateChallan={handleGenerateChallanFor}
          viewLoading={viewLoading}
          onRefresh={handleRefresh}
          statusLoading={statusLoading}
        />
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={setCurrentPage}
        loading={loading}
        totalLabel="total orders"
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {showOrderForm && (
        <OrdersForm
          isOpen={showOrderForm}
          onClose={() => { setShowOrderForm(false); setEditingOrder(null); setEditingOrderId(null); }}
          onSubmit={handleFormSubmit}
          onSuccess={async () => {
            setShowOrderForm(false);
            setEditingOrder(null);
            setEditingOrderId(null);
            try { await fetchOrders({}, { force: true }); } catch (e) { console.error('Failed to refresh after create', e); }
          }}
          order={editingOrder}
          orderId={editingOrderId}
          loading={actionLoading}
        />
      )}

      {showViewModal && (
        <OrdersViewModal
          isOpen={showViewModal}
          onClose={() => { setShowViewModal(false); setViewingOrder(null); }}
          order={viewingOrder}
          loading={viewLoading}
        />
      )}







      {showGenerateChallanModal && (
        <GenerateChallanModal
          isOpen={showGenerateChallanModal}
          orderId={generateChallanOrderId}
          onClose={() => { setShowGenerateChallanModal(false); setGenerateChallanOrderId(null); }}
          onConfirm={handleConfirmGenerateChallan}
          loading={false}
        />
      )}



      {deleteConfirm && (
        <ConfirmDialog
          isOpen
          title={deleteConfirm.title}
          message={deleteConfirm.message}
          confirmLabel={deleteConfirm.confirmLabel}
          cancelLabel={deleteConfirm.cancelLabel}
          confirmVariant={deleteConfirm.confirmVariant}
          loading={actionLoading}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {showDeleteAllModal && (
        <SuperAdminDeleteDialog
          isOpen={showDeleteAllModal}
          typeLabel="All Sales Orders"
          loading={actionLoading}
          onConfirm={handleConfirmDeleteAll}
          onCancel={() => setShowDeleteAllModal(false)}
        />
      )}

      {showBulkDeleteModal && (
        <SuperAdminDeleteDialog
          isOpen={showBulkDeleteModal}
          typeLabel={`${selectedOrders.length} Selected Orders`}
          loading={actionLoading}
          onConfirm={handleConfirmBulkDelete}
          onCancel={() => setShowBulkDeleteModal(false)}
        />
      )}

    </div>
  );
};

export default OrdersPage;
